/**
 * @fileoverview
 * Registro de primitivas (implementações) dos blocos das extensões customizadas no VM.
 * Quando uma extensão customizada é carregada, além de registrar os blocos no ScratchBlocks (UI),
 * é necessário registrar as funções que o VM executa ao rodar cada bloco.
 *
 * As primitivas recebem (args, blockUtility) e podem retornar valor ou Promise.
 * Para sensores em modo realtime, delegamos ao peripheral do dispositivo (ex.: Arduino) quando
 * disponível; caso contrário retornamos valor padrão.
 */

/**
 * Obtém o peripheral do dispositivo atualmente selecionado no runtime (ex.: Arduino).
 * @param {object} runtime - Runtime do openblock-vm
 * @returns {object|null} Peripheral ou null
 */
const getCurrentPeripheral = (runtime) => {
    if (!runtime || !runtime._device || !runtime._device.deviceId) return null;
    const deviceId = runtime._device.deviceId;
    const realId = typeof runtime.analysisRealDeviceId === 'function'
        ? runtime.analysisRealDeviceId(deviceId)
        : deviceId;
    if (!realId || !runtime.peripheralExtensions) return null;
    return runtime.peripheralExtensions[realId] || null;
};

/**
 * Retorna uma Promise com valor padrão (para quando não há peripheral ou leitura falha).
 * @param {number|boolean} value - Valor padrão
 * @returns {Promise}
 */
const defaultPromise = (value) => Promise.resolve(value);

/**
 * Mapa extensionId -> lista de opcodes que registramos (para desregistro).
 */
const OPCODES_BY_EXTENSION = {
    photoResistorSensor: ['photoResistorSensor_readValue'],
    ultrasonicSensor: ['ultrasonicSensor_readDistance'],
    linetracingIRSensor: ['linetracingIRSensor_readValue', 'linetracingIRSensor_isBlack'],
    dht11Sensor: ['dht11Sensor_readTemperature', 'dht11Sensor_readHumidity'],
    liquidCrystalI2c: ['liquidCrystalI2c_init', 'liquidCrystalI2c_printAt', 'liquidCrystalI2c_clear'],
    tb6612fng: ['tb6612fng_init', 'tb6612fng_motorA', 'tb6612fng_motorB'],
    l298n: ['l298n_init', 'l298n_motorA', 'l298n_motorB'],
    tcrt5000Digital: ['tcrt5000Digital_readValue'],
    mpu6050: ['mpu6050_init', 'mpu6050_readAccelX', 'mpu6050_readAccelY', 'mpu6050_readAccelZ', 'mpu6050_readGyroX', 'mpu6050_readGyroY', 'mpu6050_readGyroZ', 'mpu6050_readTemperature']
};

/**
 * Cria as funções primitivas para uma extensão e as registra em runtime._primitives.
 * @param {object} runtime - Runtime do openblock-vm (vm.runtime)
 * @param {string} extensionId - ID da extensão (ex.: 'photoResistorSensor')
 */
export const registerCustomExtensionPrimitives = (runtime, extensionId) => {
    if (!runtime || !runtime._primitives) return;

    const peripheral = getCurrentPeripheral(runtime);

    switch (extensionId) {
    case 'photoResistorSensor':
        runtime._primitives.photoResistorSensor_readValue = function (args, blockUtility) {
            const r = blockUtility && blockUtility.runtime ? blockUtility.runtime : runtime;
            const p = getCurrentPeripheral(r);
            if (p && typeof p.readAnalogPin === 'function') {
                return p.readAnalogPin(args.PIN || 'A0');
            }
            return defaultPromise(0);
        };
        break;

    case 'ultrasonicSensor':
        runtime._primitives.ultrasonicSensor_readDistance = function (args, blockUtility) {
            // Sensor ultrassônico normalmente usa protocolo específico; sem suporte no peripheral padrão.
            // Retorna 0 até haver implementação ou extensão de dispositivo que o suporte.
            return defaultPromise(0);
        };
        break;

    case 'linetracingIRSensor':
        runtime._primitives.linetracingIRSensor_readValue = function (args, blockUtility) {
            const r = blockUtility && blockUtility.runtime ? blockUtility.runtime : runtime;
            const p = getCurrentPeripheral(r);
            if (p && typeof p.readAnalogPin === 'function') {
                return p.readAnalogPin(args.PIN || 'A0');
            }
            return defaultPromise(0);
        };
        runtime._primitives.linetracingIRSensor_isBlack = function (args, blockUtility) {
            const r = blockUtility && blockUtility.runtime ? blockUtility.runtime : runtime;
            const p = getCurrentPeripheral(r);
            if (p && typeof p.readAnalogPin === 'function') {
                return p.readAnalogPin(args.PIN || 'A0').then(val => val < 512);
            }
            return defaultPromise(false);
        };
        break;

    case 'dht11Sensor':
        runtime._primitives.dht11Sensor_readTemperature = function (args, blockUtility) {
            // DHT11 requer protocolo específico; sem suporte no peripheral padrão.
            return defaultPromise(0);
        };
        runtime._primitives.dht11Sensor_readHumidity = function (args, blockUtility) {
            return defaultPromise(0);
        };
        break;

    case 'liquidCrystalI2c':
        // LCD I2C é usado em modo upload (código Arduino). Em realtime não há periférico; no-op.
        runtime._primitives.liquidCrystalI2c_init = function () { return defaultPromise(); };
        runtime._primitives.liquidCrystalI2c_printAt = function () { return defaultPromise(); };
        runtime._primitives.liquidCrystalI2c_clear = function () { return defaultPromise(); };
        break;

    case 'tb6612fng':
        // Ponte H TB6612 é usada em modo upload (código Arduino). Em realtime não há periférico; no-op.
        runtime._primitives.tb6612fng_init = function () { return defaultPromise(); };
        runtime._primitives.tb6612fng_motorA = function () { return defaultPromise(); };
        runtime._primitives.tb6612fng_motorB = function () { return defaultPromise(); };
        break;

    case 'l298n':
        // Ponte H L298N é usada em modo upload (código Arduino). Em realtime não há periférico; no-op.
        runtime._primitives.l298n_init = function () { return defaultPromise(); };
        runtime._primitives.l298n_motorA = function () { return defaultPromise(); };
        runtime._primitives.l298n_motorB = function () { return defaultPromise(); };
        break;

    case 'tcrt5000Digital':
        runtime._primitives.tcrt5000Digital_readValue = function (args, blockUtility) {
            const r = blockUtility && blockUtility.runtime ? blockUtility.runtime : runtime;
            const p = getCurrentPeripheral(r);
            if (p && typeof p.readDigitalPin === 'function') {
                return p.readDigitalPin(args.PIN || '2').then(val => val === 1 || val === true);
            }
            return defaultPromise(false);
        };
        break;

    case 'mpu6050':
        runtime._primitives.mpu6050_init = function () { return defaultPromise(); };
        runtime._primitives.mpu6050_readAccelX = function () { return defaultPromise(0); };
        runtime._primitives.mpu6050_readAccelY = function () { return defaultPromise(0); };
        runtime._primitives.mpu6050_readAccelZ = function () { return defaultPromise(0); };
        runtime._primitives.mpu6050_readGyroX = function () { return defaultPromise(0); };
        runtime._primitives.mpu6050_readGyroY = function () { return defaultPromise(0); };
        runtime._primitives.mpu6050_readGyroZ = function () { return defaultPromise(0); };
        runtime._primitives.mpu6050_readTemperature = function () { return defaultPromise(0); };
        break;

    default:
        break;
    }
};

/**
 * Remove do runtime as primitivas registradas para a extensão.
 * @param {object} runtime - Runtime do openblock-vm
 * @param {string} extensionId - ID da extensão
 */
export const unregisterCustomExtensionPrimitives = (runtime, extensionId) => {
    if (!runtime || !runtime._primitives) return;

    const opcodes = OPCODES_BY_EXTENSION[extensionId];
    if (opcodes) {
        opcodes.forEach(opcode => {
            delete runtime._primitives[opcode];
        });
    }
};

/**
 * Retorna os opcodes registrados para uma extensão (útil para testes ou inspeção).
 * @param {string} extensionId - ID da extensão
 * @returns {string[]}
 */
export const getOpcodesForExtension = (extensionId) => {
    return OPCODES_BY_EXTENSION[extensionId] || [];
};
